import { Placement } from '@floating-ui/dom';
import { html, nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { AutoPositionController } from '../../controllers/autoPositionOverlay';
import { SafeTriangle } from '../../controllers/safeTriangle';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksCloseEvent } from '../../events/blocks-close';
import { BlocksOpenEvent } from '../../events/blocks-open';
import { BlocksSelectEvent } from '../../events/blocks-select';
import type { AutoPositionInterface, Gap, Position, SelectValue } from '../../util/common-modal';
import { isString } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../dropdown-panel/dropdown-panel';
import '../icon/icons/check';
import '../icon/icons/chevron-down';
import '../icon/icons/chevron-right';
import type {
  FlyoutPositionType,
  FlyoutVariant,
  ListItemBehavior,
  ListItemSize,
  ListStatusType,
  NavigationMobileVariant,
  TargetType
} from '../list-item/list-item.model';
import { BlocksList } from '../list/list';
import styles from './list-item.styles';

const topBottom = ['top', 'bottom'];
const rightLeft = ['right', 'left'];
const initialPlacement: Partial<Record<Placement, Placement>> = {
  left: 'left-start',
  right: 'right-start',
  top: 'top-start',
  bottom: 'bottom-start'
};
const rtl: Partial<Record<Placement, Placement>> = {
  left: 'right',
  right: 'left'
};
const fallbacks: Partial<Record<Placement, Placement[]>> = {
  left: ['left-end', 'right-start', 'right-end'],
  right: ['right-end', 'left-start', 'left-end'],
  top: ['top-end', 'right-start', 'right-end', 'left-start', 'left-end', 'bottom-start', 'bottom-end'],
  bottom: ['bottom-end', 'right-start', 'right-end', 'left-start', 'left-end', 'top-start', 'top-end']
};
const LIST_ITEM_MARGIN = 2;
const LIST_ITEM_PADDING = 5;
/**
 * @slot default  - The component content
 * @slot before - The component before content
 * @slot after - The component after content
 * @slot items - Content in the flyout variant
 * @slot custom-link - The component's custom link slot
 * @fires bl-select - emitted when item is
 * @fires bl-flyout-open - emitted when flyout item is opened
 * @fires bl-flyout-close - emitted when flyout item is close
 * @csspart base - The component's base wrapper.
 * @csspart link - The component's link.
 * @csspart body - The component's body wrapper.
 * @csspart icon - The component's icon.
 * @csspart part-before - The component's before slot.
 * @csspart text - The component's text wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart highlighted - The component's highlighted text.
 * @csspart after-wrapper - The component's after slot wrapper.
 * @csspart safe-triangle-wrapper - The component's safe-triangle  wrapper.
 * @csspart part-after - The component's after slot.
 * @csspart icon-right - The component's `<cat-icon-chevron-right>`.
 * @csspart icon-down - The component's `<cat-icon-chevron-down>`.
 * @csspart items-wrapper - The component's items wrapper.
 * @csspart panel - The component's cat-dropdown-panel element.
 * @csspart part-items - The component's items slot.
 * @csspart items-wrapper - The component's items wrapper.
 * @csspart part-custom-link - The component's custom link slot
 */

export class BlocksListItem extends BlocksElement implements AutoPositionInterface {static styles: CSSResultGroup = styles;

  /**
   * Size
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**sm** reduces the padding on the top/bottom of the list item</li>
   * <li>**lg** increases the padding on the top/bottom of the list item</li>
   * <li>**static** renders a list item that isn't interactive. Use for typeahead dropdowns</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: ListItemSize = 'default';

  /**
   * Truthy value of isStatic property disables active, focus, hover effects for cat-list-item
   */
  @property({ type: Boolean })
  isStatic: boolean | undefined;

  /**
   * id property represents the ID attribute for cat-list-item
   */
  public itemId = nanoid();

  /**
   * Status of fieldSet
   */
  @property({ type: String })
  status: ListStatusType;

  /**
   * Disabled attribute
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  /**
   * Flyout dropdown panel variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**bare** removes border from dropdown panel in flyout menu</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  dropdownPanelVariant: FlyoutVariant;

  /**
   * Target attribute for a link (i.e. set to _blank to open in new tab)
   * - **flyout** changes the nested items into flyout menus
   * - **megamenuflyout** changes the nested items into megamenu
   */
  @property({ type: String })
  behavior: ListItemBehavior;

  /**
   * Flyout position (only use with flyout behavior)
   * - **left** positions the flyout menu to the left of the item trigger
   * - **bottom** positions the flyout menu below the item trigger
   */
  @property({ type: String })
  flyoutPosition: FlyoutPositionType;

  /**
   * URL if this is an <a> element - this swaps <button> for <a>
   */
  @property({ type: String })
  href: string | undefined;

  /**
   * Value for a list item that needs to return a value to an input in a dropdown. This is required for search-form/drop-down.
   * Value can be a string or object ({label, value} pair)
   * TODO: Removing `{type:Object}` since `SelectValue` can be an Object but also String.
   */
  @property()
  value: SelectValue | undefined;

  /**
   * Active state
   */
  @property({ type: Boolean })
  isActive: boolean | undefined;

  /**
   * Current state
   */
  @property({ type: Boolean })
  isCurrent: boolean | undefined;

  /**
   * role of list-item. Default - listitem
   * within search field role is - option
   * within primary-nav role is - menuitem
   * within utility-nav role is - menuitem
   * within contextual-menu role is - menuitem
   * within grouping first list item role to - presentation
   * flyout/expandable list set list item role to - menuitem
   */
  @property({ type: String })
  listItemRole = 'listitem';

  /**
   * Check Mark Icon
   */
  @property({ type: Boolean })
  isCheckIcon: boolean | undefined;

  /**
   * Searched String
   */
  @property({ type: String })
  searchText: string | undefined;

  /**
   * ariaPosinSet to announce list position
   */
  @property({ type: String })
  ariaposinset: string | undefined;

  /**
   * ariasetsize to account total list items
   */
  @property({ type: String })
  ariasetsize: string | undefined;

  /**
   * ariaLabel For customized label announcements.
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * If List can travel using arrow key state
   */
  @property({ type: Boolean })
  isArrowNav = true;

  /**
   * Target attribute for a link (i.e. set to _blank to open in new tab)
   * - **_blank** yields a link that opens in a new tab
   * - **_self** yields a link that loads the URL into the same browsing context as the current one. This is the default behavior
   * - **_parent** yields a link that loads the URL into the parent browsing context of the current one.
   *   If there is no parent, this behaves the same way as _self
   * - **_top** yields a link that loads the URL into the top-level browsing context. If there is no parent, this behaves the same way as _self.
   */
  @property({ type: String })
  target: TargetType;

  /**
   * Cross Axis Gap which determines the offset applied to the dropdown panel
   * @ignore
   */
  @property({ type: Number })
  crossAxisGap = -LIST_ITEM_PADDING;

  /**
   * Main Axis Gap which determines the offset between to the dropdown panel
   * @ignore
   */
  @property({ type: Number })
  mainAxisGap = LIST_ITEM_MARGIN;

  /**
   * Query the .cat-c-tooltip
   */
  @query('.cat-c-list-item__dropdown-panel')
  private _catListItemDropdown!: HTMLElement;

  /**
   * Query the list-item
   */
  @query('.cat-c-list-item__link')
  private _catListItemLink: HTMLElement | undefined;

  /**
   * Can be used to enable click or hover, default is hover
   * @ignore
   */
  @state()
  public trigger: 'hover' | 'click' = 'hover';

  /**
   * This state is used for Mega menu.
   * @ignore
   */
  @state()
  isMegaMenuFlyout: boolean | undefined;

  /**
   * This state is used for Mega menu mobile view.
   * @ignore
   */
  @state()
  isMegaMenuInMobileView: boolean | undefined;

  /**
   * This state is used for Navigation item mobile variant.
   * @ignore
   */
  @state()
  navigationMobileVariant: NavigationMobileVariant;

  /**
   * Can be used to check if `items` slot is present
   * @ignore
   */
  public get hasItemsSlot(): boolean {
    return this.slots.hasSlotted('items');
  }

  private get hasCustomLinkSlot(): boolean {
    return this.slots.hasSlotted('custom-link');
  }

  /**
   * queries all children cat-list-items
   */
  private get _childCatListItems(): NodeListOf<BlocksListItem> {
    return this.querySelectorAll('cat-list-item');
  }

  /**
   * queries all closest children cat-list-items
   */
  private get _closestChildCatListItems() {
    return this.closest('cat-list')?.querySelectorAll('cat-list-item') as NodeListOf<BlocksListItem>;
  }

  /**
   * Query the svg wrapper of safe-triangle
   */
  @query('.svg-wrapper')
  private svgWrapper: HTMLElement | undefined;

  /**
   * returns closest list item.
   */
  private get _closestListItem() {
    return this.closest('cat-list')?.closest('cat-list-item');
  }

  /**
   * returns true id variant is flyout or if it is stepped megamenu
   */
  private get flyoutBehavior() {
    return this.isFlyoutNotInMobile || this.isMegaMenuFlyout;
  }

  /**
   * returns true if behavior is drawer
   * @ignore
   */
  public get isDrawerBehavior() {
    return this.behavior === 'drawer' || this.navigationMobileVariant === 'drawer';
  }

  /**
   * returns true if behavior is flyout and if it is not a stepped megamenu
   */
  private get isOnlyFlyout() {
    return this.isFlyoutNotInMobile && !this.isMegaMenuFlyout;
  }

  /**
   * returns true id role is treeitem or menuitem
   */
  private get getListItemRole() {
    const role = ['treeitem', 'menuitem'];
    return role.includes(this.listItemRole);
  }

  /**
   * Can be used to decide listitem is used in flyout/drawer variant or not
   */
  private get underFlyoutOrDrawer(): boolean | undefined {
    return (
      this._closestListItem?.isActive &&
      (this._closestListItem?.behavior === 'flyout' || this._closestListItem?.isMegaMenuFlyout || this._closestListItem?.isDrawerBehavior)
    );
  }

  private get isClickEnabled() {
    return this.trigger === 'click';
  }

  private get isLTR() {
    return document.dir === 'ltr' || document.dir === '';
  }

  private get isFirstListItem(): boolean {
    return this.closest('cat-list')?.querySelector('cat-list-item') === this;
  }

  private get isFlyoutNotInMobile() {
    return this.behavior === 'flyout' && !this.navigationMobileVariant;
  }

  public getFloatingElement(): HTMLElement {
    return this._catListItemDropdown;
  }
  public getPosition(): Position {
    return this.getListPosition(this.flyoutPosition);
  }
  public getIsInline(): boolean {
    return false;
  }
  public getReferenceElement(): HTMLElement {
    return this._catListItemLink!;
  }
  public getAllowedPlacements(): Placement[] {
    return this.getListAllowedPlacements(this.flyoutPosition);
  }
  public getShiftCrossAxis(): boolean {
    return true;
  }
  public getGap(): Gap {
    const mainAxis = topBottom.includes(this.flyoutPosition!) ? 0 : this.mainAxisGap;
    const crossAxis = this.flyoutPosition !== 'bottom' ? (this.isFirstListItem ? this.crossAxisGap : 0) : 0;
    return { mainAxis, crossAxis };
  }

  private coordinates = { x: 0, y: 0 };

  protected autoPositionController = new AutoPositionController(this);
  protected safeTriangle = new SafeTriangle();

  public slots = new SlotController(this, 'before', 'after', 'custom-link', 'items', null);

  /**
   *@returns the root <cat-list> HTMLElement
   */
  private findCatListRootNode(node: ParentNode) {
    let activeNode = node as HTMLElement;
    let tag = activeNode.tagName.toUpperCase();
    while (tag === 'CAT-LIST-ITEM' || tag === 'CAT-LIST') {
      tag = activeNode.tagName;
      if (tag !== 'CAT-LIST-ITEM' && tag !== 'CAT-LIST') {
        break;
      }
      activeNode = activeNode.parentNode as HTMLElement;
    }

    return activeNode;
  }

  /**
   * Focus on first list item
   * 1) Used for keyboard functionality within the list
   * 2) If the list item role is not 'treeitem' and If the list item is expanded, focus on the first child item
   */
  public focusOnFirstItem(e?: Event) {
    /* 2 */
    if (this.listItemRole === 'treeitem') {
      return;
    }
    if (!this.parentNode) {
      return;
    }
    const parentNode = this.findCatListRootNode(this.parentNode);
    let catListItems;

    if (parentNode.querySelectorAll('cat-list-item.cat-is-visible').length > 0) {
      catListItems = parentNode.querySelectorAll('cat-list-item.cat-is-visible');
    } else {
      catListItems = parentNode.querySelectorAll('cat-list-item');
    }

    for (let i = 0; i < catListItems.length; i++) {
      const firstListItem = catListItems[i] as BlocksListItem;
      if (firstListItem && !firstListItem.classList.contains('cat-is-invisible') && !firstListItem.isStatic) {
        setTimeout(() => {
          firstListItem?.focus();
        });
        e?.preventDefault();
        break;
      }
    }
  }

  /**
   * Focus on last list item
   * 1) Used for keyboard functionality within the list
   * 2) If the list item role is not 'treeitem' and  If the list item is expanded, focus on the last child item
   */
  public focusOnLastItem(e: Event) {
    /* 2 */
    if (this.listItemRole === 'treeitem') {
      return;
    }
    if (!this.parentNode) {
      return;
    }

    const catListItems = this.parentNode?.children!;
    for (let i = catListItems.length - 1; i >= 0; i--) {
      const lastListItem = catListItems[i] as BlocksListItem;
      if (lastListItem) {
        const linkItem = lastListItem.shadowRoot?.querySelector<HTMLButtonElement | HTMLAnchorElement>('.cat-c-list-item__link');
        let isLastElementExpanded = linkItem?.getAttribute('aria-expanded');

        if (lastListItem.isStatic && i > 0) {
          continue; // skip static items
        }

        if (lastListItem.children.length > 0 && isLastElementExpanded === 'true') {
          this.handleUpArrowForExpandedList(e, lastListItem);
        } else {
          lastListItem?.focus(); // not expanded, focus link
        }
        e.preventDefault();
        break;
      }
    }
  }

  /**
   *sets and remove aria-hidden for the panels beneath the opened panel in drawer variant
   */
  private setInactiveForParentPanels() {
    const itemsBehindCurrentThePanel = Array.from(this._closestChildCatListItems).filter((item: BlocksListItem) => {
      return !Array.from(this._childCatListItems).includes(item);
    });
    if (this.isActive) {
      itemsBehindCurrentThePanel.forEach((item: BlocksListItem) => {
        item.setAttribute('aria-hidden', 'true');
      });
    } else {
      itemsBehindCurrentThePanel.forEach((item: BlocksListItem) => {
        item.removeAttribute('aria-hidden');
      });
    }
  }

  /**
   * Connected callback lifecycle
   * 1) Add event listeners
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.handleOnClickOutside, false);
    document.addEventListener('mousemove', this.handleMouseMove);
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove window resize event listener
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOnClickOutside, false);
    document.removeEventListener('mousemove', this.handleMouseMove);
  }

  /**
   * Handles the mouse move event and updates the coordinates.
   */
  private handleMouseMove = (event: MouseEvent) => {
    this.coordinates.x = event.clientX;
    this.coordinates.y = event.clientY;
    this.requestUpdate();
  };

  /**
   * First updated lifecycle when component is first updated on page
   */
  protected firstUpdated() {
    this.findParentListItem();
    this._catListItemLink?.addEventListener('blur', this.removeFocusClass);
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isActive') && !this.isActive) {
      this.closeAllChildListItems();
    }

    if (_changedProperties.has('isActive') && this.isOnlyFlyout && this.hasItemsSlot) {
      if (this.isActive) {
        this.autoPositionController.openOverlay();
      } else {
        this.autoPositionController.closeOverlay();
      }
    }
    if (_changedProperties.has('isActive') && this.isDrawerBehavior && this.hasItemsSlot) {
      this.setInactiveForParentPanels();
    }
  }

  /**
   * Get the initial placement
   */
  private getListPosition(position: Placement = 'right'): Placement {
    return !this.isLTR && rightLeft.includes(position) ? initialPlacement[rtl[position]!]! : initialPlacement[position]!;
  }

  /**
   * Get the fallback placements
   */
  private getListAllowedPlacements(placement: Placement = 'right'): Placement[] {
    return !this.isLTR && rightLeft.includes(placement) ? fallbacks[rtl[placement]!]! : fallbacks[placement]!;
  }

  /**
   * Handle click outside the component
   * Check if the behavior is 'flyout' or the trigger is 'hover'
   * 1) Close the show hide panel on click outside
   * 2) If the nav is already closed then we don't care about outside clicks and we
   * can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active navigation item
   * 5) If the navigation is active and we've clicked outside of the nav then it should
   * be closed.
   */
  private handleOnClickOutside = (event: MouseEvent) => {
    if (this.isFlyoutNotInMobile || this.trigger) {
      /* 2 */
      if (!this.isActive) {
        return;
      }

      /* 3 */
      if (!this.shadowRoot?.host) {
        throw Error('Could not determine navigation context during click handler');
      }

      /* 4 */
      const didClickInside = event.composedPath().includes(this.shadowRoot.host);

      if (this.isActive && !didClickInside) {
        this.handleMouseLeave();
      }
    }
  };

  /**
   * Get the deeper active element
   */
  private getActiveElement(document: Document | ShadowRoot, activeElement?: Element): Element | null | undefined {
    if (document.activeElement?.shadowRoot?.activeElement) {
      return this.getActiveElement(document.activeElement.shadowRoot, document.activeElement);
    }
    return activeElement;
  }

  /**
   * @returns the focused list item id
   */
  private getActiveElementId() {
    const activeElement = this.getActiveElement(document);
    if (activeElement instanceof BlocksListItem) {
      return activeElement.itemId;
    }
  }

  /**
   * @returns Next active Item in the list according to the List hierarchy
   * 1.Default Code flow other than flyout menu.
   * 2.Flow to be chosen when it is flyout variant.
   */
  private getNextActiveItem(): BlocksListItem | undefined {
    const activeElementId = this.getActiveElementId();
    const listItemElementId = (this.childNodes[0]?.parentElement as BlocksListItem)?.itemId;

    const isListItemExpanded = this._catListItemLink?.getAttribute('aria-expanded');
    const checkActiveElement = activeElementId && listItemElementId && activeElementId === listItemElementId;

    /**1 */
    if (!this.underFlyoutOrDrawer) {
      if (checkActiveElement && this.children?.length > 0 && isListItemExpanded === 'true') {
        return this.getNextFocusableElement(this.querySelector('cat-list')?.children[0] as BlocksListItem);
      } else {
        return this.getNextFocusableElement(this.nextElementSibling as BlocksListItem);
      }
    } else {
      /**2 */
      if (checkActiveElement && this.nextElementSibling) {
        return this.getNextFocusableElement(this.nextElementSibling as BlocksListItem);
      } else if (this.nextElementSibling === null) {
        return this.getNextFocusableElement(this.closest('cat-list')?.children[0] as BlocksListItem);
      }
    }
  }

  /** * Returns the next cat-list-item element in the list, starting from the provided element * @param startElement The element to start checking from * @returns The next cat-list-item element, or null if none found
   * 1. Loop through siblings until a focusable element is found
   * 2. Check if the current element is a 'cat-list-item' and is not static
   * 3. Move to the next sibling
   * 4. Return default element if no focusable element found
   */
  private getNextFocusableElement(startElement: HTMLElement): BlocksListItem {
    let currentElement: HTMLElement = startElement;

    /*1*/
    while (currentElement) {
      if (currentElement.tagName.toLowerCase() === 'cat-list-item' && !(currentElement as BlocksListItem).isStatic) {
        /*2*/
        return currentElement as BlocksListItem;
      }

      currentElement = currentElement.nextElementSibling as HTMLElement; /*3*/
    }

    return currentElement as BlocksListItem; /*4*/
  }

  /** * Returns the previous cat-list-item element in the list, starting from the provided element * @param startElement The element to start checking from * @returns The previous cat-list-item element, or null if none found
   * 1. Loop through previous siblings until a cat-list-item is found
   * 2. Check if the current element is a 'cat-list-item' and is not static
   * 3. Move to the previous sibling
   * 4. Return default element if no focusable element found
   */
  private getPreviousFocusableElement(startElement: BlocksListItem): BlocksListItem {
    let currentElement = startElement;

    /*1*/
    while (currentElement) {
      /*2*/
      if (currentElement.tagName.toLowerCase() === 'cat-list-item' && !(currentElement as BlocksListItem).isStatic) {
        return currentElement as BlocksListItem;
      }
      currentElement = currentElement.previousElementSibling as BlocksListItem; /*3*/
    }

    return currentElement as BlocksListItem; /*4*/
  }
  /**
   *
   * @returns Previous active Item in the list according to the List hierarchy
   * 1.Default Code flow other than flyout menu.
   * 2.Flow to be chosen when it is flyout varian
   */
  private getPreviousActiveItem() {
    const activeElementId = this.getActiveElementId();
    const listItemElementId = (this.childNodes?.[0]?.parentElement as BlocksListItem)?.itemId;

    const isListItemExpanded = this.previousElementSibling?.shadowRoot
      ?.querySelector<HTMLInputElement>('.cat-c-list-item__link')
      ?.getAttribute('aria-expanded');

    const checkActiveElement = activeElementId && listItemElementId && activeElementId === listItemElementId;

    /**1 */
    if (!this.underFlyoutOrDrawer) {
      if (checkActiveElement && this.previousElementSibling && this.previousElementSibling?.children.length > 0 && isListItemExpanded === 'true') {
        const previousSiblingsChildren = this.previousElementSibling?.querySelector('cat-list')?.children;
        return this.getPreviousFocusableElement(previousSiblingsChildren![previousSiblingsChildren!.length - 1] as BlocksListItem);
      } else {
        return this.getPreviousFocusableElement(this.previousElementSibling as BlocksListItem);
      }
    } else {
      /**2 */
      if (checkActiveElement && this.previousElementSibling !== null && this.previousElementSibling.tagName.toLowerCase() === 'cat-list-item') {
        return this.getPreviousFocusableElement(this.previousElementSibling as BlocksListItem);
      } else if (this.previousElementSibling === null || !(this.previousElementSibling?.tagName.toLowerCase() === 'cat-list-item')) {
        const closestCatList = this.closest('cat-list');
        const lastChildInList = closestCatList?.children[closestCatList?.children.length - 1] as BlocksListItem;
        return this.getPreviousFocusableElement(lastChildInList);
      }
    }
  }

  /**
   * 1) If right or down arrow key is struck and radio field exists after current item, remove checked from all items and
   * add checked to the next item. Focus on this item and set tabindex for when focusing out of radio field and back onto checked item. If Flyout is there then this code block is considered.
   * 2) If down arrow key is stuck after current item, then this ElseIf condition checks whether it's parent item has nextSibling or not.
   * If it's parent item has nextSibling, then it traverse to its parent item's nextSibling.
   */
  private focusOnDownArrowPress(e: KeyboardEvent) {
    const isListItemExpanded = this._catListItemLink?.getAttribute('aria-expanded');
    const catListItems = this._closestChildCatListItems;
    const lastListItem = catListItems[catListItems.length - 1] as BlocksListItem;

    if (lastListItem && lastListItem.isStatic && this.nextElementSibling === lastListItem) {
      // Focus on the first available focusable item
      this.focusOnFirstItem(e);
      return;
    } else if (this.nextElementSibling && this.nextElementSibling?.tagName === 'CAT-LIST') {
      // Focus on the first available link list item in the next sibling list group
      const nextListGroup = this.nextElementSibling as BlocksList;
      let firstListItem = nextListGroup.querySelector('cat-list-item') as BlocksListItem;
      while (firstListItem?.isStatic) {
        firstListItem = firstListItem.nextElementSibling as BlocksListItem;
      }
      firstListItem?.focus();
    } else if (this.nextElementSibling || (this.children?.length > 0 && isListItemExpanded === 'true') || this.underFlyoutOrDrawer) {
      /* 1 */
      let next = this.getNextActiveItem();
      if (next) {
        this.focusOnUpcomingItem(e, next, true);
      }
    } else if (this.parentElement?.parentElement?.tagName === 'CAT-LIST-ITEM') {
      /* 2 */
      let nextItem = this.parentElement.parentElement?.nextElementSibling as BlocksListItem;

      if (nextItem) {
        this.focusOnUpcomingItem(e, nextItem, true);
      } else {
        this.handleDownArrowForExpandedList(e, this.parentElement.parentElement as BlocksListItem);
      }
    } else {
      this.focusOnFirstItem(e);
    }
  }

  /**
   * 1) If left or up arrow key is struck and radio field exists before current item, remove checked from all items and
   * add it to the next item.If Flyout is there then this code block is considered.
   * 2) If up arrow key is stuck after current item, then this ElseIf condition checks whether it has parent item or not. If it has parent item,
   * then it traverse to its parent item.
   */
  private focusOnUpArrowPress(e: KeyboardEvent) {
    if (!this.parentNode) {
      return;
    }
    const firstListItem = this._closestChildCatListItems[0] as BlocksListItem;

    // Check if the first item is static
    if (
      firstListItem &&
      firstListItem.isStatic &&
      this.previousElementSibling === firstListItem &&
      !(this.parentElement?.previousElementSibling?.tagName === 'CAT-LIST-ITEM')
    ) {
      // Focus on the last available focusable item
      this.focusOnLastItem(e);
      return;
    } else if (this.parentElement?.previousElementSibling?.tagName === 'CAT-LIST-ITEM') {
      // Focus on the previous sibling list item
      const previousListItem = this.parentElement.previousElementSibling as BlocksListItem;
      previousListItem?.focus();
    } else if ((this.previousElementSibling && !this.previousElementSibling.classList.contains('cat-is-invisible')) || this.underFlyoutOrDrawer) {
      /* 1 */
      let previous = this.getPreviousActiveItem();
      if (previous) {
        this.focusOnUpcomingItem(e, previous);
      }
    } else if (this?.parentElement?.parentElement?.tagName === 'CAT-LIST-ITEM') {
      /* 2 */
      let previousItem = this.parentElement?.parentElement as BlocksListItem;
      if (previousItem) {
        this.focusOnPreviousActiveItem(e, previousItem);
        this.listItemRole !== 'treeitem' && e.stopPropagation();
      } else {
        this.handleUpArrowForExpandedList(e, this.parentElement as BlocksList);
      }
    } else {
      this.focusOnLastItem(e);
    }
  }
  /**
   * Function to be executed on Right Arrow press in flyout variant.It will open submenu and focus will be shifted to 1st item of submenu.
   * 1.Dispatch event to handle dynamic width in stepped megamenu variant in primary nav.
   * 2. If it is drawer variant, focus the back button when the panel is opened
   * 3. Call dispatch event method on opening flyout
   */
  private focusOnRightArrowPress(kbEvent: KeyboardEvent) {
    if (this.flyoutBehavior && !this.isActive) {
      this.isActive = true;
      this.handleFlyoutOpened(); /*3*/
      requestAnimationFrame(() => {
        this.focusNextRightActiveItem(kbEvent);
      });
      /* 1*/
      if (this.isMegaMenuFlyout) {
        this.dispatch({
          eventName: 'bl-set-dynamic-width',
          detailObj: { activeElement: this.getActiveElement(document) }
        });
      }
    } else if (this.isDrawerBehavior && !this.isActive) {
      /* 2*/
      this.isActive = true;
      this.focusListBackButton();
    }
  }

  private focusListBackButton() {
    requestAnimationFrame(() => {
      const backButton = this.querySelector<BlocksList>('cat-list')?._catListBackButton;
      if (backButton) {
        backButton.focus();
      }
    });
  }
  /**
   * Function to be executed on Left Arrow press in flyout variant.
   * 1.Dispatch event to handle dynamic width in stepped megamenu variant in primary nav.
   * 2.To stop event bubbling.
   * 3.Call dispatch event method on closing flyout menu.
   */
  private focusOnLeftArrowPress(kbEvent: KeyboardEvent) {
    const previous = this._closestListItem;
    const navlink = previous?.shadowRoot?.querySelector('.cat-c-primary-nav__item.cat-is-active > .cat-c-primary-nav__link');
    if (navlink || !previous) {
      return;
    }
    this.focusOnUpcomingItem(kbEvent, previous);
    previous.isActive = false;
    /*1*/
    this.dispatch({
      eventName: 'bl-set-dynamic-width',
      detailObj: { activeElement: this.getActiveElement(document) }
    });
    kbEvent.stopPropagation(); /*2*/
    this.handleFlyoutClosed(); /*3*/
  }

  /**
   *
   * @returns Truthy value if current listItem is focussed
   */
  private isCurrentItemActive() {
    const currentItemID = this.itemId;
    const activeItemID = this.getActiveElementId();
    return currentItemID === activeItemID;
  }

  private focusNextRightActiveItem(kbEvent: KeyboardEvent) {
    const activeElementId = this.getActiveElementId();
    const listItemElementId = this.itemId;
    requestAnimationFrame(() => {
      if (activeElementId && listItemElementId && activeElementId === listItemElementId) {
        const next = this.getNextFocusableElement(this.querySelector('cat-list')?.children[0] as BlocksListItem);
        if (next) {
          this.focusOnUpcomingItem(kbEvent, next, true);
        }
      }
    });
  }

  /**
   * Handle keydown
   * 1) If item is clicked, navigate to the custom link
   */
  private handleCustomLinkClick(e: MouseEvent) {
    if (e.type === 'click') {
      this._children?.[0].click(); /* 1 */
    }
  }

  /**
   * Handle keydown
   * 1) filters actually focussed item.
   * 2) If key selected is enter, navigate to the custom link
   */
  private handleKeyDown(e: KeyboardEvent) {
    if (this.disabled && e.key !== KEYS.TAB) {
      e.preventDefault();
    }
    const isCurrentItemFocussed = this.isCurrentItemActive();

    if (this.hasCustomLinkSlot) {
      if (e.key === KEYS.ENTER) {
        this._children?.[0].click(); /* 2 */
      }
    }

    if (!isCurrentItemFocussed || !this.isArrowNav || this.listItemRole === 'treeitem') {
      /* 1 */
      return;
    }

    if (e.key === KEYS.DOWN_ARROW) {
      //Down Arrow with flyout variant
      this.focusOnDownArrowPress(e);
    } else if ((this.flyoutBehavior || this.isDrawerBehavior) && e.key === KEYS.RIGHT_ARROW) {
      // Right Arrow
      this.focusOnRightArrowPress(e);
    } else if (e.key === KEYS.UP_ARROW) {
      //Up Arrow
      this.focusOnUpArrowPress(e);
    } else if (this.underFlyoutOrDrawer && (e.key === KEYS.LEFT_ARROW || e.key === KEYS.ESCAPE)) {
      //Left Arrow for flyout variant or escape key
      this.focusOnLeftArrowPress(e);
    } else if ((this.flyoutBehavior || this.isDrawerBehavior) && (e.key === KEYS.ENTER || e.key === KEYS.SPACE)) {
      //Enter/Space key for flyout variant
      e.preventDefault();
      this.focusOnRightArrowPress(e);
    }
    if (e.key === KEYS.LEFT_ARROW && !this._closestListItem && this.navigationMobileVariant === 'drawer') {
      this.dispatch({
        eventName: 'bl-list-item-left-arrow',
        detailObj: {}
      });
    }
  }

  /**
   * This function is used to focus the list item which is available
   */
  private focusOnPreviousActiveItem(event: KeyboardEvent | Event, focusableItem: BlocksListItem) {
    event.preventDefault();

    const focusableElement = focusableItem?.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-list-item__link');
    if (focusableElement) {
      while (focusableItem) {
        focusableItem?.focus();
        break;
      }
    }
  }

  /**
   * This function is used to focus the next item in
   * Expandable/flyout Lists.
   * 1) This block focusses item if it is available
   * 2) This block focusses the nextSiblingItem
   * 3) This block focusses the parent list item
   */
  private handleDownArrowForExpandedList(event: KeyboardEvent, parentElement: BlocksListItem) {
    event.preventDefault();

    let focusableItem = parentElement;
    while (focusableItem.tagName === 'CAT-LIST-ITEM') {
      const nextListItem = focusableItem?.nextElementSibling as BlocksListItem;

      if (nextListItem) {
        /* 1 */
        nextListItem?.focus();
        break;
      } else if (nextListItem) {
        /* 2 */
        focusableItem = nextListItem;
      } else {
        /* 3 */
        focusableItem = focusableItem.parentElement?.parentElement as BlocksListItem;
      }
    }

    if (focusableItem.tagName !== 'CAT-LIST-ITEM') {
      /* 4 */
      this.focusOnFirstItem(event);
    }
  }

  /**
   * This function is used to decide whether Item can be focusable or not.
   * 1) This ElseIf condition checks whether it's parent item has nextSibling or not.
   * If it's parent item has nextSibling, then it traverse to its parent item's nextSibling
   */
  private focusOnUpcomingItem(event: KeyboardEvent, focusableItem: BlocksListItem, isElementNextSibling = false) {
    let focusableElement = focusableItem?.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-list-item__link');
    if (focusableElement) {
      while (focusableItem) {
        event.preventDefault();
        // Consider isListItemExpanded when children list items available
        const expandedListItem = focusableElement?.querySelector('.cat-c-list-item__items');
        const isListItemExpanded = expandedListItem && focusableElement?.getAttribute('aria-expanded');

        if (isElementNextSibling || (isListItemExpanded !== 'true' && !isElementNextSibling)) {
          focusableItem?.focus();
          break;
        }

        if (isElementNextSibling && focusableItem.nextElementSibling) {
          // Get next sibling
          focusableItem = focusableItem.nextElementSibling as BlocksListItem;
        } else if (isElementNextSibling && focusableItem.parentElement?.parentElement?.tagName === 'CAT-LIST-ITEM') {
          this.handleDownArrowForExpandedList(event, focusableItem.parentElement.parentElement as BlocksListItem);
          break;
        } else if (focusableItem?.children.length > 0) {
          focusableItem = focusableItem?.children[0].lastElementChild as BlocksListItem;
        } else if (!isElementNextSibling && focusableItem.previousElementSibling) {
          // Get previous sibling
          focusableItem = focusableItem.previousElementSibling as BlocksListItem;
        } else if (focusableItem?.parentElement?.parentElement?.tagName === 'CAT-LIST-ITEM') {
          const newFocusableItem = focusableItem?.parentElement?.parentElement as BlocksListItem;
          newFocusableItem?.focus();
          break;
        } else if (isElementNextSibling && focusableItem.nextElementSibling?.tagName === 'CAT-LIST') {
          // Focus on the first available link list item in the next sibling list group
          const nextListGroup = focusableItem.nextElementSibling as BlocksList;
          const firstListItem = nextListGroup.querySelector('cat-list-item') as BlocksListItem;
          firstListItem?.focus();
          break;
        } else if (isElementNextSibling) {
          this.focusOnFirstItem(event);
          break;
        } else {
          this.focusOnLastItem(event);
          break;
        }

        focusableElement = focusableItem?.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-list-item__link');
      }
    }
  }

  /**
   * This function is used to focus the previous item in
   * Expandable/flyout Lists.
   * 1) This block gets previous item
   * 2) This block focusses item if it is collapsed
   * 3) This block gets the last item in the previous sibling ParentListItem
   */
  private handleUpArrowForExpandedList(event: KeyboardEvent | Event, parentElement: BlocksListItem | BlocksList) {
    event.preventDefault();

    const lastElementChild =
      parentElement?.lastElementChild?.tagName === 'CAT-LIST' ? parentElement?.lastElementChild?.lastElementChild : parentElement?.lastElementChild;
    if (lastElementChild?.tagName === 'CAT-LIST-ITEM') {
      let lastElement = lastElementChild as BlocksListItem;
      let focusableLastChildElement = lastElement?.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-list-item__link');
      let isLastElementExpanded = focusableLastChildElement?.getAttribute('aria-expanded');

      if (isLastElementExpanded === 'true') {
        while (lastElement) {
          const lastChildElement = (lastElement.children?.length > 0 ? lastElement.children[0]?.lastElementChild : lastElement) as BlocksListItem;
          const lastFocusableElement = lastChildElement.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-list-item__link');

          isLastElementExpanded = lastFocusableElement?.getAttribute('aria-expanded');

          if (lastChildElement) {
            /* 1 */
            lastElement = lastChildElement.previousElementSibling as BlocksListItem;
          } else if (isLastElementExpanded !== 'true') {
            /* 2 */
            (lastChildElement as BlocksListItem)?.focus();
            break;
          } else {
            /* 3 */
            lastElement = lastChildElement;
          }
        }
      } else if (lastElement?.children.length > 0) {
        lastElement?.focus();
      } else {
        this.focusOnPreviousActiveItem(event, lastElement);
      }
    }
  }

  private handleClose(e: KeyboardEvent) {
    if (e.key === KEYS.ESCAPE && this.listItemRole !== 'treeitem') {
      this.isActive = false;
    }
  }

  /**
   * Handles the mouse enter event.
   * Activates the panel if trigger is not click and the element is not disabled.
   * 1) Adds the safetriangle SVG wrapper to the top layer using popover
   */
  private handleMouseEnter() {
    if (!this.isClickEnabled && !this.disabled) {
      if (this.isFlyoutNotInMobile || this.isMegaMenuFlyout) {
        //setTimeout is added because the click is called immediately after mouseenter. This causes isActive to be toggled to false, which results in the panel closing immediately after it is opened on mobile.
        setTimeout(() => {
          this.isActive = true;
        });
        this.svgWrapper?.showPopover(); /* 1 */
      }
    }
  }

  /**
   * Handles the mouse enter event.
   * Close the panel if trigger is not click and the element is not disabled.
   * 1) Removes the safetriangle SVG wrapper from the top layer
   */
  private handleMouseLeave() {
    if (!this.isClickEnabled && !this.disabled) {
      if ((this.isFlyoutNotInMobile || this.isMegaMenuFlyout) && this.isActive) {
        setTimeout(() => {
          this.isActive = false;
        }, 300);
        this.svgWrapper?.hidePopover(); /* 1 */
      }
    }
  }

  /**
   * Handle click
   * 1) If parent is contextual menu and the item doesn't have subitems, close the contextual menu
   * 2) If the parent is a search form, close the dropdown if item is selected
   * @fires select - Fires when the list item  is clicked
   */

  private handleClick() {
    if (this.isClickEnabled && this.isMegaMenuFlyout) {
      this.updateActiveStatusSteppedMenu();
    } else if (this.hasItemsSlot) {
      this.isActive = !this.isActive;
      this.isActive ? this.handleFlyoutOpened() : this.handleFlyoutClosed();
      if (this.isDrawerBehavior) {
        this.focusListBackButton();
      }
    } else {
      this.onItemClick();
    }
  }

  /**
   * collapses all expandable list-items
   */
  private closeAllChildListItems() {
    this._childCatListItems.forEach((listItem: BlocksListItem) => {
      if (listItem.slots.hasSlotted('items')) {
        listItem.isActive = false;
      }
    });
  }

  /**
   * Update status active status for stepped mega menu
   */
  private updateActiveStatusSteppedMenu(): void {
    if (this.hasItemsSlot) {
      this.isActive = !this.isActive;
      this.isActive ? this.handleFlyoutOpened() : this.handleFlyoutClosed();
    }
  }

  /** Event to be dispatched on opening a flyout menu */
  private handleFlyoutOpened() {
    requestAnimationFrame(() => {
      this.dispatch({
        eventName: 'bl-flyout-open',
        detailObj: {
          isActive: this.isActive
        }
      }) as BlocksOpenEvent;
    });
  }

  /** Event to be dispatched on closing flyout menu */
  handleFlyoutClosed() {
    this.dispatch({
      eventName: 'bl-flyout-close',
      detailObj: {
        isActive: this.isActive
      }
    }) as BlocksCloseEvent;
  }

  onItemClick() {
    this.dispatch({
      eventName: 'bl-select',
      detailObj: {
        value: this.value
      }
    }) as BlocksSelectEvent;
  }

  /** To check Substring and assign scss to substring. check if substring has special characters and escape
     escape it has any. Escaping special characters in regular expressions is essential for accurately defining patterns that match
     specific strings or formats.To ensure that special characters are interpreted as literal characters within the string being matched,
      preventing unintended behavior and runtime errors, ensuring the regex operates as intended.
   */
  private checkSubstring(label: string) {
    if (this.searchText && !this.disabled && label) {
      const searchText = this.searchText.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$&');

      const parts = label.split(new RegExp(`(${searchText})`, 'gi'));

      return html`${parts.map((part) =>
        part.toLowerCase() === this.searchText?.toLowerCase().trim()
          ? html`<mark part="highlighted" class="cat-c-list-item__highlighted">${part}</mark>`
          : html`${part}`
      )}`;
    } else {
      return label;
    }
  }

  private renderItem() {
    let label;
    if (isString(this.value)) {
      label = this.value;
    } else {
      label = this.value?.label ?? '';
    }
    return this.checkSubstring(label.trim());
  }

  focus(options?: FocusOptions): void {
    this._catListItemLink?.focus(options);
    !this.isStatic && this._catListItemLink?.classList.add('cat-c-list-item__link-focus-active');
  }

  /**
   * @ignore
   */
  removeFocusClass = () => {
    this._catListItemLink?.classList.remove('cat-c-list-item__link-focus-active');
  };
  /**
   * This function checks for a maximum of 3 levels of nested "cat-list-item" elements.
   * 1) To find the closest parent "cat-list-item" of the current element.
   * 2) It then checks if the parent has a grandparent "cat-list-item".
   * 3) If a grandparent exists, it calls the setNestedItems method on the grandparent, otherwise it calls it on the parent.
   */
  private findParentListItem() {
    const parentClosest = this.closest('cat-list-item') as BlocksListItem; /* 1 */
    if (parentClosest) {
      const grandParentClosest = parentClosest.closest('cat-list')?.closest('cat-list-item') as BlocksListItem; /* 2 */
      if (grandParentClosest) {
        /* 3 */
        grandParentClosest.setNestedItems();
      } else {
        parentClosest.setNestedItems();
      }
    }
  }

  /**
   * Set nested items
   * 1) Gets the depth of the list item in the list
   * 2) Loop through all the lists inside the items container
   * 3) If the depth is 0, then apply 16px padding to the left to make the link indented (Subitem)
   * 4) If the depth is 2, then apply 32px padding to the left to make the link indented (Subsubitem)
   * 5) If the list item is not an instance of BlocksListItem, then return
   */
  private setNestedItems() {
    if (this.hasItemsSlot && this.behavior !== 'flyout' && this.behavior !== 'drawer') {
      /* 1 */
      let depth = 0;
      let currentNode = this as any;
      let currentNodeParent = this.parentNode?.parentNode as HTMLElement;
      while (currentNode !== currentNodeParent && currentNode !== null) {
        if (currentNodeParent.tagName === 'CAT-LIST-ITEM') {
          depth++;
        }
        currentNode = currentNode.parentNode;
      }

      /* 2 */
      const lists = this.shadowRoot
        ?.querySelector('.cat-c-list-item__items')
        ?.querySelector('slot')
        ?.assignedElements({ flatten: false }) as BlocksList[];
      if (!lists) {
        return;
      }
      lists.forEach((list: BlocksList) => {
        const listItems = list.shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])')?.assignedElements() as BlocksListItem[];
        listItems?.forEach((listItem: BlocksListItem) => {
          /* 5 */
          if (!(listItem instanceof BlocksListItem)) return;

          /* 3 */
          if (depth == 0) {
            listItem.style.setProperty('--cat-list-item-link-padding-inline-start', '16px');
          }
          /* 4 */
          if (depth == 2) {
            listItem.style.setProperty('--cat-list-item-link-padding-inline-start', '32px');
          }
        });
      });
    }
  }

  /**
   * @returns value for aria-selected if role is 'option' or 'listitem'
   */
  private getAriaSelected(): 'true' | 'false' | undefined {
    if (this.disabled && this.listItemRole === 'treeitem') {
      return 'false';
    }
    if (this.listItemRole !== 'menuitem' && this.listItemRole !== 'listitem' && this.listItemRole !== 'treeitem') {
      return this.isActive ? 'true' : 'false';
    }
  }

  /**
   * Determines the dropdown-panel placement based on whether it is a megaMenu-flyout,
   * or using the auto position controller's placement.
   * @returns {boolean} - Returns true or false based on the conditions.
   */
  private isPanelPositionedRight(): boolean {
    if (this.isMegaMenuFlyout) {
      return !this.isLTR ? false : true;
    } else {
      return this.autoPositionController.placement === 'right-start';
    }
  }

  /**
   * Set role to list and anchor/button element
   * 1) In case of menu-item, li role should be none and anchor's role should be menu-item.
   * This will avoid dual swipe gestures in mobile TalkBack mode
   */

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-list-item', {
      'cat-c-list-item-static': this.isStatic && !this.hasItemsSlot,
      'cat-c-list-item--flyout': this.isFlyoutNotInMobile,
      'cat-c-list-item--drawer': this.isDrawerBehavior,
      'cat-c-list-item--megamenuflyout': this.isMegaMenuFlyout,
      'cat-c-list-item--flyout-position-left': this.flyoutPosition === 'left',
      'cat-c-list-item--flyout-position-bottom': this.flyoutPosition === 'bottom',
      'cat-c-list-item--flyout-position-top': this.flyoutPosition === 'top',
      'cat-c-list-item--small': this.size === 'sm',
      'cat-c-list-item--large': this.size === 'lg',
      'cat-c-list-item--dropdown-panel-bare': this.dropdownPanelVariant === 'bare',
      'cat-is-current': this.isCurrent,
      'cat-is-active': this.isActive,
      'cat-is-error': this.status === 'error',
      'cat-is-disabled': this.disabled,
      'cat-c-list-item--has-slot-before': this.slots.hasSlotted('before'),
      'cat-c-list-item--has-slot-after': this.slots.hasSlotted('after'),
      'cat-c-list-item-navigation-mobile': this.navigationMobileVariant,
      'cat-c-lis-item--has-items-slot': this.hasItemsSlot
    });

    const roleForList = this.listItemRole === 'listitem' ? this.listItemRole : 'none';
    const roleForButton = this.listItemRole === 'listitem' ? undefined : this.listItemRole;

    if (this.isStatic && !this.hasItemsSlot) {
      return html`
        <li
          part="base"
          .role=${this.listItemRole}
          aria-selected=${ifDefined(this.getAriaSelected())}
          aria-current=${ifDefined((this.isCurrent && this.getListItemRole) || undefined)}
          class="${componentClassName}"
        >
          <div part="link" class="cat-c-list-item__link">
            <span part="body" class="cat-c-list-item__body">
              <div part="icon" class="cat-c-list-item__icon"><slot part="part-before" name="before"></slot></div>
              <div part="text" class="cat-c-list-item__text">
                <slot part="part-default"></slot>
              </div>
            </span>
            <div part="after-wrapper" class="cat-c-list-item__after"><slot part="part-after" name="after"></slot></div>
          </div>
        </li>
      `;
    } else if (this.href) {
      return html`
        <li part="base" .role=${roleForList} class="${componentClassName}" @keydown=${this.handleKeyDown}>
          <a
            part="link"
            aria-selected=${ifDefined(this.getAriaSelected())}
            .role=${ifDefined(roleForButton) /** 1 */}
            aria-current=${ifDefined((this.isCurrent && this.getListItemRole) || undefined)}
            class="cat-c-list-item__link cat-c-list-item-href__link"
            aria-posinset=${ifDefined(this.ariaposinset)}
            aria-setsize=${ifDefined(this.ariasetsize)}
            aria-label=${ifDefined(this.arialabel ?? undefined)}
            href=${this.href}
            target=${ifDefined(this.target)}
            aria-disabled=${ifDefined(this.disabled)}
          >
            <span part="body" class="cat-c-list-item__body">
              <div part="icon" class="cat-c-list-item__icon"><slot part="part-before" name="before"></slot></div>
              <div part="text" class="cat-c-list-item__text">
                <slot part="part-default"></slot>
              </div>
            </span>
            <div part="after-wrapper" class="cat-c-list-item__after"><slot part="part-after" name="after"></slot></div>
          </a>
        </li>
      `;
    } else if (this.hasCustomLinkSlot) {
      return html` <li part="base" role=${roleForList} class="${componentClassName}" @keydown=${this.handleKeyDown}>
        <div
          part="link"
          role=${ifDefined(roleForButton)}
          aria-selected=${ifDefined(this.isActive ? 'true' : 'false')}
          aria-current=${ifDefined((this.isCurrent && this.getListItemRole) || undefined)}
          aria-label=${ifDefined(this.arialabel ?? undefined)}
          class="cat-c-list-item__link cat-c-list-item-href__link"
          aria-disabled=${ifDefined(this.disabled)}
          @click=${this.handleCustomLinkClick}
          tabindex=${0}
        >
          <span part="body" class="cat-c-list-item__body">
            <div part="text" class="cat-c-list-item__text">
              <slot part="part-custom-link" name="custom-link" tabindex="-1"></slot>
            </div>
          </span>
        </div>
      </li>`;
    } else {
      return html`
        <li
          part="base"
          .role=${roleForList}
          class="${componentClassName}"
          @keydown=${this.handleKeyDown}
          @mouseenter=${this.handleMouseEnter}
          @mouseleave=${this.handleMouseLeave}
        >
          <button
            part="link"
            .role=${ifDefined(roleForButton)}
            aria-posinset=${ifDefined(this.ariaposinset)}
            aria-setsize=${ifDefined(this.ariasetsize)}
            aria-label=${ifDefined(this.arialabel ?? undefined)}
            aria-current=${ifDefined((this.isCurrent && this.getListItemRole) || undefined)}
            aria-selected=${ifDefined(this.getAriaSelected())}
            class="cat-c-list-item__link"
            @click=${this.handleClick}
            aria-disabled=${ifDefined(this.disabled)}
            aria-expanded=${ifDefined(this.hasItemsSlot ? (this.isActive === true ? true : false) : undefined)}
            aria-haspopup=${ifDefined((this.hasItemsSlot && this.listItemRole === 'menuitem') || undefined)}
          >
            <span part="body" class="cat-c-list-item__body">
              <div part="icon" class="cat-c-list-item__icon"><slot part="part-before" name="before"></slot></div>
              <div part="text" class="cat-c-list-item__text"><slot part="part-default">${this.renderItem()}</slot></div>
              <span part="icon" class="cat-c-check-mark__icon">
                ${this.isActive && this.isCheckIcon
                  ? html` <cat-icon-check part="icon-after" exportparts=${this.getExportParts('check', 'icon')} slot="after"></cat-icon-check> `
                  : html`${nothing}`}
              </span>

              ${this.isFlyoutNotInMobile || this.isMegaMenuFlyout || this.isMegaMenuInMobileView
                ? html` <div part="after-wrapper" class="cat-c-list-item__after"><slot part="part-after" name="after"></slot></div> `
                : nothing}
              ${this.hasItemsSlot
                ? this.isFlyoutNotInMobile || this.isDrawerBehavior || this.isMegaMenuFlyout
                  ? html`
                      <cat-icon-chevron-right
                        part="icon-right"
                        exportparts=${this.getExportParts('chevron-right', 'icon')}
                        class="cat-c-list-item__items-icon cat-c-list-item-chevron-right"
                      ></cat-icon-chevron-right>
                    `
                  : html`
                      <cat-icon-chevron-down
                        part="icon-down"
                        exportparts=${this.getExportParts('chevron-down', 'icon')}
                        class="cat-c-list-item__items-icon"
                      ></cat-icon-chevron-down>
                    `
                : nothing}
            </span>

            ${this.behavior !== 'flyout' && !this.isMegaMenuFlyout && !this.isMegaMenuInMobileView
              ? html` <div part="after-wrapper" class="cat-c-list-item__after"><slot part="part-after" name="after"></slot></div> `
              : nothing}
          </button>
          ${this.isFlyoutNotInMobile || this.isDrawerBehavior || this.isMegaMenuFlyout
            ? html`
                <div part="safe-triangle-wrapper" class="svg-wrapper" popover="manual">
                  ${(this.isActive &&
                    this._catListItemDropdown &&
                    this.safeTriangle.generateSafeTriangle(this._catListItemDropdown, this.isPanelPositionedRight(), this.coordinates)) ||
                  nothing}
                </div>
                <div
                  part="items-wrapper"
                  class="cat-c-list-item__items"
                  @mouseenter=${!this.isClickEnabled ? this.handleMouseEnter : nothing}
                  aria-hidden=${ifDefined(!this.isActive ? 'true' : undefined)}
                  .role=${this.isMegaMenuFlyout ? this.listItemRole : ''}
                >
                  <cat-dropdown-panel
                    part="panel"
                    exportparts=${this.getExportParts('dropdown-panel')}
                    variant=${ifDefined(this.dropdownPanelVariant)}
                    class="cat-c-list-item__dropdown-panel ${this.isOnlyFlyout ? 'cat-c-dropdown-panel-only-flyout' : ''}"
                    @keydown=${this.handleClose}
                    .role=${this.isMegaMenuFlyout ? 'menu' : ''}
                    popover=${ifDefined(this.isOnlyFlyout ? 'manual' : undefined)}
                  >
                    <slot part="part-items" name="items"></slot>
                  </cat-dropdown-panel>
                </div>
              `
            : html`
                <div part="items-wrapper" class="cat-c-list-item__items" aria-hidden=${ifDefined(!this.isActive ? 'true' : undefined)}>
                  <slot part="part-items" name="items"></slot>
                </div>
              `}
        </li>
      `;
    }
  }
}

if (customElements.get('cat-list-item') === undefined) {
  customElements.define('cat-list-item', BlocksListItem);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-list-item': BlocksListItem;
  }
}
